/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ActionReducerMapBuilder } from "@reduxjs/toolkit";

import { LocaleKeys } from "../../../model/locale";
import { localizedErrorFallback } from "../../../model/utility";
import type { SignInState } from "../signIn.types";
import { sessionInvalidated } from "../signIn.actions";
import { rehydrateTokenFromStorage } from "../thunks/token.thunks";

/**
 * Reducers for the session marker the route guards read.
 *
 * `domainToken` is not a credential — TokenManager owns the token and every
 * call site resolves it there. What this slice tracks is only whether a
 * session exists, which is what `selectIsAuthenticated` reports.
 *
 * There is no separate refresh case: refreshing happens inside TokenManager,
 * transparently to the store. A `null` payload is the ordinary "no session"
 * outcome — nothing persisted, or nothing renewable — and hands over to the
 * sign-in orchestration. The rejected case is narrower: the restore itself
 * failed unexpectedly, which is worth a message rather than a silent retry.
 */
export function registerTokenReducers(
  builder: ActionReducerMapBuilder<SignInState>
): void {
  builder.addCase(sessionInvalidated, (state) => {
    state.domainToken = null;
  });
  builder.addCase(rehydrateTokenFromStorage.pending, (state) => {
    state.isTokenRehydrating = true;
    state.isRefreshingToken = true;
    state.refreshError = "";
  });
  builder.addCase(rehydrateTokenFromStorage.fulfilled, (state, action) => {
    state.domainToken = action.payload;
    state.isTokenRehydrating = false;
    state.isRefreshingToken = false;
    state.refreshError = "";
  });
  builder.addCase(rehydrateTokenFromStorage.rejected, (state, action) => {
    state.isTokenRehydrating = false;
    state.isRefreshingToken = false;
    state.refreshError =
      action.payload !== undefined && action.payload !== null
        ? action.payload
        : localizedErrorFallback(
            LocaleKeys.SIGN_IN_SESSION_REFRESH_ERROR_MESSAGE
          );
    // No usable session: guards short-circuit to the sign-in screen.
    state.domainToken = null;
  });
}
